import { Component, ViewChild, ElementRef, computed, effect, input, signal } from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import { AngularSvgIconModule } from "angular-svg-icon";
import { LoaderComponent } from "../../../../../../components/common/loader/loader";
import { AccessPointType } from "../../../../../../gql/generated-ingestor";

@Component({
  selector: "access-points",
  templateUrl: "access-points.html",
  styleUrls: ["access-points.scss"],
  imports: [AngularSvgIconModule, LoaderComponent, TranslateModule],
})
export class AccessPoints {
  protected accessPoints = input.required<AccessPointType[] | null>();
  protected accessPointSummaries = signal<AccessPointSummary[]>([]);

  @ViewChild("scrollarea") scrollarea: ElementRef;
  protected rightEnabled = signal(false);
  protected leftEnabled = signal(false);

  protected isLoading = computed(() => this.accessPointSummaries() === null);
  protected hasAccessPoints = computed(() => this.accessPointSummaries().length !== 0);

  constructor() {
    effect(() => this.accessPointSummaries.set(this.summarizeAccessPoints(this.accessPoints())));
  }

  summarizeAccessPoints(accessPoints: AccessPointType[]): AccessPointSummary[] {
    type AccessPointCounts = {
      [brand: string]: {
        [model: string]: {
          [status: string]: number;
          total: number;
        };
      };
    };

    const accessPointCounts: AccessPointCounts = {};

    accessPoints.forEach(({ brand, model, ciStatus: status }) => {
      accessPointCounts[brand] ??= {};
      accessPointCounts[brand][model] ??= { total: 0 };
      accessPointCounts[brand][model][status] ??= 0;

      accessPointCounts[brand][model][status] += 1;
      accessPointCounts[brand][model].total += 1;
    });

    return Object.entries(accessPointCounts).flatMap(([brand, models]) =>
      Object.entries(models).map(([model, counts]) => ({
        brand,
        model,
        active: counts["Active"] ?? "-",
        inactive: counts["Stopped"] ?? "-",
        total: counts.total ?? "-",
      })),
    );
  }

  scrollLeft() {
    this.scrollarea.nativeElement.scrollLeft -= 150;

    setTimeout(() => {
      this.checkScroll();
    }, 500);
  }

  scrollRight() {
    this.scrollarea.nativeElement.scrollLeft += 150;
    setTimeout(() => {
      this.checkScroll();
    }, 500);
  }

  checkScroll() {
    this.leftEnabled.set(this.scrollarea.nativeElement.scrollLeft !== 0);

    const newScrollLeft = this.scrollarea.nativeElement.scrollLeft;
    const width = this.scrollarea.nativeElement.clientWidth;
    const scrollWidth = this.scrollarea.nativeElement.scrollWidth;
    this.rightEnabled.set(scrollWidth - (newScrollLeft + width) !== 0);
  }
}

interface AccessPointSummary {
  brand: string;
  model: string;
  active: number | string;
  inactive: number | string;
  total: number | string;
}
